/*
 * File: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice\src\Router\Router.tsx
 * Project: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice
 * Created Date: Tuesday June 6th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday, 22nd January 2024 10:31:07
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { createBrowserRouter } from "react-router-dom";
import Root from "Content/Root/Root";
import ErrorPage from "Content/Error/ErrorPage/ErrorPage";
import RouterPathResolver from "Config/Router/RouterPathResolver";
import Login from "Content/Login/Login";
import HomePage from "Content/HomePage/HomePage";
import CarService from "Services/Car/CarService";
import { ExtractQueryInObj, InitRequestParams } from "Services/RouterService/RouterService";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "./AuthProvider";
import ServiceGeneral from "Services/ServiceGeneral";
import UserService from "Services/Users/UserService";
import CustomerPage from "Content/User/CustomerPage";
import UserSingle from "Content/User/UserSingle";
import Company from "Content/Company/Company";
import CompanyService from "Services/CompanyService/CompanyService";
import MyCustomerPage from "Content/User/MyCustomerPage";
import MyCompanyPage from "Content/User/MyCompanyPage";
import HomeService from "Services/Home/HomeService";
import Stats from "Content/Stats/Stats";
import ParksList from "Content/Parks/Parks";
import ParkService from "Services/ParkService/ParkService";
import CompanyList from "Content/Company/CompanyList/CompanyList";
import CarList from "Content/Car/CarList/CarList";
import Car from "Content/Car/Car";
import SeenCarService from "Services/SeenCar/SeenCar.service";
import OfferTabs from "Content/Offers/OfferTabs/offerTabs";
import { UserDataProvider } from "Hooks/useUserContext";
import MyCommercialPage from "Content/User/Commercials/MyCommercialPage";
import MessageService from "Services/Message/MessageService";
import EditMessage from "Content/Message/EditMessage";
import SendMessage from "Content/Message/SendMessage";
import CollectionService from "Services/Visuals/Collection/CollectionService";
import Visuals from "Content/Visuals/Visuals";

const Router = () => {
  const { logout } = useAuth();

  return createBrowserRouter([
    {
    path: `${RouterPathResolver.pathEntryPoint.path}`,
    element: <UserDataProvider><Root children={[]} /></UserDataProvider>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: `${RouterPathResolver.pathLogin.path}`,
        element: <Login />
      },
      {
        path: `${RouterPathResolver.pathEntryPoint.path}`,
        loader: async () => {
          return [
            HomeService.getUsersInfo(logout),
            HomeService.getCarsInfo(logout),
            HomeService.getOfferInfo(logout)
          ];
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathLogin.path}><HomePage /></ProtectedRoute>,
        errorElement: <Login />,
      },
      {
        path: `${RouterPathResolver.pathUser.path}`,
        loader: ({request}) => {
          const myParams = InitRequestParams(request.url);
          return [
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, per_page: 9999, status: "valid", role: "CLIENT"})},() => logout()),
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({per_page: 999, status: "notValid", role: "CLIENT"})}, () => logout()),
            ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({per_page: 999, status: "archived", role: "CLIENT"})}, () => logout())
          ];
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CustomerPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathMyCommercial.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse(() => { return UserService.getCommercials()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><MyCommercialPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathMyUser.path}`,
        loader: async ({request}) => {
          const myParams = InitRequestParams(request.url);
          return ServiceGeneral.manageAxiosResponse( () => { return UserService.getMyUsers({...myParams, perPage: 9999, status: "valid"})}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><MyCustomerPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathStats.path}`,
        loader: ({request}) => {
          return ExtractQueryInObj(request);
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Stats/></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathEditMessage.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return MessageService.getAllMessages()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><EditMessage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathSendMessage.path}`,
        loader: async ({request}) => {
          const myParams = InitRequestParams(request.url);
          return [
            await ServiceGeneral.manageAxiosResponse( () => { return MessageService.getAllMessages()}, () => logout()),
            await ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, per_page: 20, status: "valid", role: "CLIENT"})},() => logout())
          ]
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><SendMessage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathVisuals.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return CollectionService.getCollectionsName()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Visuals /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathOffers.path}`,
        loader: ({request}) => {
          return ExtractQueryInObj(request);
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><OfferTabs /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathMyCompany.path}`,
        loader: async ({request}) => {
          const myParams = InitRequestParams(request.url);
          return [
            await ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, status: "valid", role: "NOTCUSTOMER", per_page:999, includeRole: true})}, () => logout()),
            await ServiceGeneral.manageAxiosResponse( () => { return UserService.getUsers({...myParams, status: "archived", role: "NOTCUSTOMER", per_page:999, includeRole: true})}, () => logout())
          ]
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><MyCompanyPage /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathUser_BY_ID.path}`,
        loader: async ({params, request}) => {
          const myParams = InitRequestParams(request.url);
          return Promise.all([
            ServiceGeneral.manageAxiosResponse(() => {return UserService.getUserByID(params.id!)}, () => logout()),
            ServiceGeneral.manageAxiosResponse(() => {return UserService.getUserFavorites(params.id!)}, () => logout()),
            ServiceGeneral.manageAxiosResponse(() => {return SeenCarService.getAllView({userid: params.id!})}, () => logout()),
          ]).then(([userData, favorites, seenCars]) => {
            if (userData === null || userData === undefined)
              throw Error("No data loaded.");
            return {userData, usertab: myParams.usertab, offertab: myParams.offertab, favorites, seenCars: seenCars.seenCar};
          }).catch(error => {
            throw new Error("Unable to load user data");
          });
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><UserSingle /></ProtectedRoute>,
      },
      {
        path: `${RouterPathResolver.pathCar_BY_ID.path}`,
        loader: async ({params, request})=> {
          const myParams = InitRequestParams(request.url);
          return {
            editable: myParams.editable,
            id: params.id!,
            promises: [
              ServiceGeneral.manageAxiosResponse(() => {return CarService.getCarByID(params.id!)}, () => logout()),
              ServiceGeneral.manageAxiosResponse(() => {return CarService.getCarPictures(params.id!)}, () => logout())
            ]
          };
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Car /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathParks.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return ParkService.getAllParks()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><ParksList /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCompanies.path}`,
        loader: async () => {
          return await ServiceGeneral.manageAxiosResponse( () => { return CompanyService.getAllCompanies()}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CompanyList /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCompany_BY_ID.path}`,
        loader: async ({params, request}) => {
          const myParams = InitRequestParams(request.url);
          return await ServiceGeneral.manageAxiosResponse( () => { return CompanyService.getCompanyByID(params.id!, {...myParams})}, () => logout());
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><Company /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      },
      {
        path: `${RouterPathResolver.pathCar.path}`,
        loader: async ({request}) => {
          return ExtractQueryInObj(request);
        },
        element: <ProtectedRoute condition={"isConnected === true"} redirectPath={RouterPathResolver.pathHome.path}><CarList /></ProtectedRoute>,
        errorElement: <ErrorPage />,
      }
    ]
  },
]);
}

export default Router;
